import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { AppHelper } from "../../shared/appHelper/appHelper";
import { Permission } from "../../Models/permission";
import { TServiceResult } from "../../shared/Infra/TServiceResult";
import { ServiceResult } from "../../shared/Infra/ServiceResult";
import { ServiceBase } from '../service-base/service-base';
import { LocalRepository } from '../data/repository/local-repository';
import { share, switchMap } from 'rxjs/operators';
import { PERMISSIONS } from '../data/permissions';
import { ACCESS } from '../data/access-map';


@Injectable()
export class PermissionService extends ServiceBase<any>{

  permissionUrl = `${AppHelper.SSoApi}Permission`;

  private _currentUserPermissionsSubject: ReplaySubject<TServiceResult<Permission[]>>;

  constructor(http: HttpClient,
    repo: LocalRepository) {
    super("", http, repo)
  }


  getUserPermissionsByTerm(userId: string, branchId: number,
    term: string): Observable<TServiceResult<Permission[]>> {
    return this.http.get<TServiceResult<Permission[]>>(this.permissionUrl +
      '/GetUserPermissionsByTerm?userId=' + userId +
      '&branchId=' + branchId + '&term=' + term)
  }

  /** Subscribe to this before accessing the static ACCESS object or PERMISSIONS array */
  getCurrentUserPermissions(): Observable<TServiceResult<Permission[]>> {
    if (this._currentUserPermissionsSubject && !this._currentUserPermissionsSubject.isStopped) {
      
      return this._currentUserPermissionsSubject.pipe(share());
    }

    this._currentUserPermissionsSubject = new ReplaySubject<TServiceResult<Permission[]>>(1);


    return this.http.get<TServiceResult<Permission[]>>(this.permissionUrl + '/GetCurrentUserPermissions')
      .pipe(switchMap(result => {
        if (result.successful) {
          this.setAccessMap(result.returnValue);
          this._currentUserPermissionsSubject.next(result);
          return of(result);
        }
        else {
          // this._currentUserPermissionsSubject.error(err);
          // this._currentUserPermissionsSubject = null;
          result.returnValue = null;
          this._currentUserPermissionsSubject=null;
          return of(new TServiceResult<Permission[]>(false,'',null));
        }
      }));

  }

  getRolePermissionsByTerm(roleId: string, branchId: number,
    term: string): Observable<TServiceResult<Permission[]>> {
    return this.http.get<TServiceResult<Permission[]>>(this.permissionUrl +
      '/GetRolePermissionsByTerm?roleId=' + roleId +
      '&branchId=' + branchId + '&term=' + term)
  }

  setUserPermission(resourceId: number, userId: string, branchId?: number,
    accessRight?: boolean): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(this.permissionUrl +
      '/SetUserPermission?resourceId=' + resourceId + '&userId=' + userId +
      '&branchId=' + branchId + '&accessRight=' + accessRight, resourceId)
  }

  setRolePermission(resourceId: number, roleId: string, branchId?: number,
    accessRight?: boolean): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(this.permissionUrl +
      '/SetRolePermission?resourceId=' + resourceId + '&roleId=' + roleId +
      '&branchId=' + branchId + '&accessRight=' + accessRight, resourceId)
  }

  removePermission(permissionId: number): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(this.permissionUrl +
      '/AssignUserPermission?permissionId=' + permissionId, permissionId)
  }



  clearCaches() {
    this._currentUserPermissionsSubject = null;
  }

  private setAccessMap(permissions: Permission[]) {

    PERMISSIONS.splice(0);
    ACCESS.FamilyViewAccess = false;
    ACCESS.FamilyAccess = false;
    ACCESS.MemberAccess = false;
    ACCESS.MemberViewAccess = false;
    ACCESS.OtherMightyServesAccess = false;
    ACCESS.FamilyWithoutNationalCodeAccess = false;
    ACCESS.CategoryReferenceMedical = false;
    ACCESS.ProblemViewAccess = false;
    ACCESS.ProblemAccess = false;
    ACCESS.UserViewAccess = false;
    ACCESS.UserAccess = false;
    ACCESS.BranchViewAccess = false;
    ACCESS.BranchAccess = false;
    ACCESS.RoleViewAccess = false;
    ACCESS.RoleAccess = false;
    ACCESS.AllTransactionsAccessView = false;
    ACCESS.AddNewTransaction = false;
    ACCESS.HebehAccess = false;
    ACCESS.EventLogViewAccess = false;
    ACCESS.FamilyViewAccess = false;
    ACCESS.FamilyAccess = false;
    ACCESS.ProblemViewAccess = false;
    ACCESS.ProblemAccess = false;
    ACCESS.UserViewAccess = false;
    ACCESS.UserAccess = false;
    ACCESS.BranchViewAccess = false;
    ACCESS.BranchAccess = false;
    ACCESS.PlanViewAccess = false;
    ACCESS.PlanAccess = false;
    ACCESS.DocumentViewAccess = false;
    ACCESS.DocumentAccess = false;
    ACCESS.DashboardViewAccess = false;
    ACCESS.DashboardUserActivityInLastMonth = false;
    ACCESS.DashboardBranchActivityInLastMonth = false;
    ACCESS.DashboardActivateBranchesInLastMonth = false;
    ACCESS.DashboardActivateUsersInLastMonth = false;
    ACCESS.DashboardHebeh = false;
    ACCESS.DashboardSubBranches = false;
    ACCESS.BranchChangeParent = false;
    ACCESS.ReportSearchZone = false;
    ACCESS.ReportHasNationalCode = false;
    ACCESS.ReportIsEnable = false;
    ACCESS.ReportInformationStatus = false;
    ACCESS.ReportRegisterDate = false;
    ACCESS.ReportBirthDayDate = false;
    ACCESS.ReportGenderType = false;
    ACCESS.ReportDocumentStatus = false;
    ACCESS.ReportHeadOfHouseHoldStatus = false;
    ACCESS.ReportFundamentalProblem = false;
    ACCESS.ReportNeedyLevel = false;
    ACCESS.ReportHouseStatus = false;
    ACCESS.ReportSponsors = false;
    ACCESS.ReportSocialHarms = false;
    ACCESS.ReportAddress = false;
    ACCESS.ReportMarriedStatus = false;
    ACCESS.ReportInsuranceStatus = false;
    ACCESS.ReportInsuranceType = false;
    ACCESS.ReportAccountInformation = false;
    ACCESS.ReportBranchFilter = false;
    ACCESS.AccessRight = false;
    ACCESS.ChangeParentBranch = false;
    ACCESS.ChangeFamilyBranch = false;
    ACCESS.ChangeBranchRole = false;
    ACCESS.ExportExcel = false;
    ACCESS.ExportExcelName = false;
    ACCESS.ExportExcelLastName = false;
    ACCESS.ExportExcelNationalCode = false;
    ACCESS.ExportExcelFatherName = false;
    ACCESS.ExportExcelEducationLevel = false;
    ACCESS.ExportExcelMaritalStatus = false;
    ACCESS.ExportExcelBirthCity = false;
    ACCESS.ExportExcelBirthDate = false;
    ACCESS.ExportExcelMonthlyExpense = false;
    ACCESS.ExportExcelBranch = false;
    ACCESS.ExportExcelActiveStatus = false;
    ACCESS.ExportExcelAddress = false;
    ACCESS.CategoryViewAccess = false;
    ACCESS.CategoryAccess = false;
    ACCESS.CategoryFeatureViewAccess = false;
    ACCESS.CategoryFeatureAccess = false;
    ACCESS.FamilyDeactiveAccess = false;
    ACCESS.FamilyDeactiveAccess = false;
    ACCESS.BranchProblemViewAccess = false;
    ACCESS.ProMiServeAccess = false;
    ACCESS.ChatSupportAccess = false;
    ACCESS.ProblemStatusAccess = false;
    ACCESS.VerifyProblemAccess = false;
    ACCESS.VerifyBranchRequestView = false;
    ACCESS.AllMightyServesAccessView = false;
    ACCESS.CampaignCategoryAccess = false;
    ACCESS.SayehAdmin = false;
    ACCESS.ChangeStatusGalleryAccess = false;
    ACCESS.ChangeStatusAdminCheckListAccess = false;
    ACCESS.MemberReportAccess = false;
    ACCESS.ChangeStatusProblemFeatureAnswerAccess = false;
    ACCESS.EditProblemFeatureAnswerImageAccess = false;
    for (let item of permissions) {
      PERMISSIONS.push(item);
      if (item.resource.resourceName == "FamilyViewAccess") { ACCESS.FamilyViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "FamilyAccess") { ACCESS.FamilyAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "MemberAccess") { ACCESS.MemberAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "MemberViewAccess") { ACCESS.MemberViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "OtherMightyServesAccess") { ACCESS.OtherMightyServesAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "FamilyWithoutNationalCodeAccess") { ACCESS.FamilyWithoutNationalCodeAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "CategoryReferenceMedical") { ACCESS.CategoryReferenceMedical = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ProblemViewAccess") { ACCESS.ProblemViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ProblemAccess") { ACCESS.ProblemAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "UserViewAccess") { ACCESS.UserViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "UserAccess") { ACCESS.UserAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "BranchViewAccess") { ACCESS.BranchViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "BranchAccess") { ACCESS.BranchAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "RoleViewAccess") { ACCESS.RoleViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "RoleAccess") { ACCESS.RoleAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "AllTransactionsAccessView") { ACCESS.AllTransactionsAccessView = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "AddNewTransaction") { ACCESS.AddNewTransaction = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "HebehAccess") { ACCESS.HebehAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "EventLogViewAccess") { ACCESS.EventLogViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "FamilyViewAccess") { ACCESS.FamilyViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "FamilyAccess") { ACCESS.FamilyAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ProblemViewAccess") { ACCESS.ProblemViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ProblemAccess") { ACCESS.ProblemAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "UserViewAccess") { ACCESS.UserViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "UserAccess") { ACCESS.UserAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "BranchViewAccess") { ACCESS.BranchViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "BranchAccess") { ACCESS.BranchAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "PlanViewAccess") { ACCESS.PlanViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "PlanAccess") { ACCESS.PlanAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DocumentViewAccess") { ACCESS.DocumentViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DocumentAccess") { ACCESS.DocumentAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardViewAccess") { ACCESS.DashboardViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardUserActivityInLastMonth") { ACCESS.DashboardUserActivityInLastMonth = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardBranchActivityInLastMonth") { ACCESS.DashboardBranchActivityInLastMonth = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardActivateBranchesInLastMonth") { ACCESS.DashboardActivateBranchesInLastMonth = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardActivateUsersInLastMonth") { ACCESS.DashboardActivateUsersInLastMonth = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardHebeh") { ACCESS.DashboardHebeh = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "DashboardSubBranches") { ACCESS.DashboardSubBranches = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "BranchChangeParent") { ACCESS.BranchChangeParent = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportSearchZone") { ACCESS.ReportSearchZone = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportHasNationalCode") { ACCESS.ReportHasNationalCode = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportIsEnable") { ACCESS.ReportIsEnable = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportInformationStatus") { ACCESS.ReportInformationStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportRegisterDate") { ACCESS.ReportRegisterDate = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportBirthDayDate") { ACCESS.ReportBirthDayDate = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportGenderType") { ACCESS.ReportGenderType = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportDocumentStatus") { ACCESS.ReportDocumentStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportHeadOfHouseHoldStatus") { ACCESS.ReportHeadOfHouseHoldStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportFundamentalProblem") { ACCESS.ReportFundamentalProblem = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportNeedyLevel") { ACCESS.ReportNeedyLevel = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportHouseStatus") { ACCESS.ReportHouseStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportSponsors") { ACCESS.ReportSponsors = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportSocialHarms") { ACCESS.ReportSocialHarms = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportAddress") { ACCESS.ReportAddress = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportMarriedStatus") { ACCESS.ReportMarriedStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportInsuranceStatus") { ACCESS.ReportInsuranceStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportInsuranceType") { ACCESS.ReportInsuranceType = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportAccountInformation") { ACCESS.ReportAccountInformation = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ReportBranchFilter") { ACCESS.ReportBranchFilter = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "AccessRight") { ACCESS.AccessRight = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChangeParentBranch") { ACCESS.ChangeParentBranch = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChangeFamilyBranch") { ACCESS.ChangeFamilyBranch = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChangeBranchRole") { ACCESS.ChangeBranchRole = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcel") { ACCESS.ExportExcel = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelName") { ACCESS.ExportExcelName = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelLastName") { ACCESS.ExportExcelLastName = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelNationalCode") { ACCESS.ExportExcelNationalCode = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelFatherName") { ACCESS.ExportExcelFatherName = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelEducationLevel") { ACCESS.ExportExcelEducationLevel = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelMaritalStatus") { ACCESS.ExportExcelMaritalStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelBirthCity") { ACCESS.ExportExcelBirthCity = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelBirthDate") { ACCESS.ExportExcelBirthDate = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelMonthlyExpense") { ACCESS.ExportExcelMonthlyExpense = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelBranch") { ACCESS.ExportExcelBranch = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelActiveStatus") { ACCESS.ExportExcelActiveStatus = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ExportExcelAddress") { ACCESS.ExportExcelAddress = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "CategoryViewAccess") { ACCESS.CategoryViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "CategoryAccess") { ACCESS.CategoryAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "CategoryFeatureViewAccess") { ACCESS.CategoryFeatureViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "CategoryFeatureAccess") { ACCESS.CategoryFeatureAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "FamilyDeativeAccess") { ACCESS.FamilyDeactiveAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "MapViewAccess") { ACCESS.FamilyDeactiveAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "BranchProblemViewAccess") { ACCESS.BranchProblemViewAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ProMiServeAccess") { ACCESS.ProMiServeAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChatSupportAccess") { ACCESS.ChatSupportAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ProblemStatusAccess") { ACCESS.ProblemStatusAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "VerifyProblemAccess") { ACCESS.VerifyProblemAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "VerifyBranchRequestView") { ACCESS.VerifyBranchRequestView = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "AllMightyServesAccessView") { ACCESS.AllMightyServesAccessView = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "CampaignCategoryAccess") { ACCESS.CampaignCategoryAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "SayehAdmin") { ACCESS.SayehAdmin = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChangeStatusGalleryAccess") { ACCESS.ChangeStatusGalleryAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChangeStatusAdminCheckListAccess") { ACCESS.ChangeStatusAdminCheckListAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "MemberReportAccess") { ACCESS.MemberReportAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "ChangeStatusProblemFeatureAnswerAccess") { ACCESS.ChangeStatusProblemFeatureAnswerAccess = item.aggregateAccessRight; }
      else if (item.resource.resourceName == "EditProblemFeatureAnswerImageAccess") { ACCESS.EditProblemFeatureAnswerImageAccess = item.aggregateAccessRight; }
    }
  }


}
